import { Injectable, Provider } from '@angular/core';
import { RoomSelectionService } from '../room-selection.service';
import { LeadTaskService } from '../../lead-task.service';
import { ErrorListService } from '../../../../reusable-components/error-list/error-list.service';
import { MatStepperService } from '../../lead-task-services/mat-stepper.service';
import {
  RoomClosedReason,
  translateUsageSpaceCategory,
  UsageSpaceCategory,
  UsageSpaceInfo,
} from '../../../../lead.model';
import {
  ImageWithDescription,
  RoomExaminationResultInfo,
} from '../../../../examinationResult.model';

export function provideRoomExaminationService(): Provider {
  return {
    provide: RoomExaminationService,
    useFactory: (
      roomSelectionService: RoomSelectionService,
      leadTaskService: LeadTaskService,
      matStepperService: MatStepperService,
    ) =>
      new RoomExaminationService(
        roomSelectionService,
        leadTaskService,
        matStepperService,
      ),
    deps: [RoomSelectionService, LeadTaskService, MatStepperService],
  };
}

@Injectable()
export class RoomExaminationService {
  public readonly errorListService: ErrorListService;
  public readonly errorListServiceTariffSelection: ErrorListService;
  public readonly errorListServiceRoomNotVisited: ErrorListService;

  // FR-01, FR-02: i18n keys for save-blocking validation errors
  private readonly sizeValidationError =
    'components.room-examination.size-zero-error';
  private readonly categoryValidationError =
    'components.room-examination.category-null-error';

  constructor(
    private readonly roomSelectionService: RoomSelectionService,
    private readonly leadTaskService: LeadTaskService,
    private readonly matStepperService: MatStepperService,
  ) {
    this.errorListService =
      this.roomSelectionService.getRoomExaminationInput().errorListService;
    this.errorListServiceTariffSelection =
      this.errorListService.create('tariff-selection');
    this.errorListServiceRoomNotVisited =
      this.errorListService.create('room-not-visited');
    this.updateValidationErrors();
  }

  getNgComponentOutletInputObservable() {
    return this.roomSelectionService.ngComponentOutletInput$;
  }

  public isSelfCreatedRoom(): boolean {
    return this.leadTaskService.getSelfCreatedRoomsIds().includes(this.getId());
  }

  private getRoomDraftInfo(): RoomExaminationResultInfo {
    return this.roomSelectionService.getRoomExaminationInput().roomDraftCopy;
  }

  private getId(): string {
    return this.roomSelectionService.getRoomExaminationInput().id;
  }

  public getRoomIsClosed(): boolean {
    return this.getRoomDraftInfo().roomIsClosed;
  }

  public setRoomIsClosed(roomIsClosed: boolean): void {
    this.getRoomDraftInfo().roomIsClosed = roomIsClosed;
    this.updateValidationErrors();
  }

  public getRoomIsClosedReason(): RoomClosedReason | undefined {
    return this.getRoomDraftInfo().roomIsClosedReason;
  }

  public setRoomIsClosedReason(
    roomClosedReason: RoomClosedReason | undefined,
  ): void {
    this.getRoomDraftInfo().roomIsClosedReason = roomClosedReason;
  }

  private getUsageSpaceInfo(): UsageSpaceInfo {
    return this.roomSelectionService.getRoomExaminationInput()
      .usageSpaceInfoCopy;
  }

  saveRoomExamination() {
    this.leadTaskService.setCurrentRoomExaminationId(undefined);
    this.leadTaskService.addOrUpdateRoomExaminationResult({
      id: this.getId(),
      info: this.getRoomDraftInfo(),
    });
    this.roomSelectionService.startRoomList();
    if (
      this.leadTaskService
        .getUsageSpaceIds()
        .every((usageSpaceId: string) =>
          this.leadTaskService.hasRoomDraft(usageSpaceId),
        )
    ) {
      this.matStepperService.next();
    }
  }

  discardRoomExamination() {
    this.leadTaskService.removeRoomExaminationResult(this.getId());
    this.roomSelectionService.startRoomList();
  }

  /**
   * getter and setter for roomexamination
   */

  getImages(): string[] {
    return this.getRoomDraftInfo().images.map(
      (image: ImageWithDescription) => image.image,
    );
  }

  addImage(image: string): void {
    this.getRoomDraftInfo().images.push({ image: image });
  }

  removeImageWithDescription(index: number): void {
    this.getRoomDraftInfo().images.splice(index, 1);
  }

  addDescription(index: number, description: string): void {
    this.getRoomDraftInfo().images[index].description = description;
  }

  getDescription(index: number): string {
    return this.getRoomDraftInfo().images[index].description || '';
  }

  public getName(): string {
    const roomDraft = this.getRoomDraftInfo();
    if (roomDraft.name !== undefined) {
      return roomDraft.name;
    }
    return this.getUsageSpaceInfo().name;
  }

  public setName(name: string): void {
    if (this.leadTaskService.validateUsageSpaceName(name)) {
      this.getRoomDraftInfo().name = name;
    } else {
      console.error('Try to set inalid usage space name to Draft');
    }
  }

  getNumberRooms(): number {
    return this.getUsageSpaceInfo().numberRooms;
  }

  getTranslatableCategory(): string {
    return translateUsageSpaceCategory(this.getCategory());
  }

  getCategory(): UsageSpaceCategory {
    const roomDraft = this.getRoomDraftInfo();
    if (roomDraft.usageSpaceCategory != null) {
      return roomDraft.usageSpaceCategory as UsageSpaceCategory;
    }
    return this.getUsageSpaceInfo().category ?? 'UNBEKANNT';
  }

  setCategory(category: UsageSpaceCategory): void {
    this.getRoomDraftInfo().usageSpaceCategory = category;
    this.updateValidationErrors();
  }

  // FR-03: hotel/guesthouse rooms are exempt from size validation
  public isHotelGaestezimmer(): boolean {
    return this.getCategory() === 'HOTEL_GAESTEZIMMER';
  }

  getSize(): number {
    const roomDraft = this.getRoomDraftInfo();
    if (roomDraft.size !== undefined) {
      return roomDraft.size;
    }
    return this.getUsageSpaceInfo().size;
  }

  public setSize(size: string): void {
    if (this.leadTaskService.validateUsageSpaceSize(size)) {
      this.getRoomDraftInfo().size = Number.parseInt(size);
    } else {
      console.error('Try to set inalid usage space size');
    }
    this.updateValidationErrors();
  }

  // FR-01, FR-02, FR-03: add or remove save-blocking errors based on current state
  private updateValidationErrors(): void {
    // FR-01: size must be > 0 when music is detected (room not closed) and not a hotel room
    if (!this.getRoomIsClosed() && !this.isHotelGaestezimmer()) {
      if (this.getSize() <= 0) {
        this.errorListService.addError(this.sizeValidationError);
      } else {
        this.errorListService.removeError(this.sizeValidationError);
      }
    } else {
      this.errorListService.removeError(this.sizeValidationError);
    }

    // FR-02: category must not be null/UNBEKANNT — runtime SAP data may be null despite TypeScript type
    const category = this.getCategory();
    if (category && category !== 'UNBEKANNT') {
      this.errorListService.removeError(this.categoryValidationError);
    } else {
      this.errorListService.addError(this.categoryValidationError);
    }
  }
}
